import { FlagImage } from 'react-international-phone';
import { useEffect, useRef, useState } from 'react';
import cn from 'clsx';

import styles from './PhoneInput.module.css';

interface LazyFlagImageProps {
  iso2: string;
  alt?: string;
  className?: string;
}

/**
 * A wrapper around FlagImage that defers loading until the image is near the viewport.
 * Uses Intersection Observer to detect when the flag should be loaded.
 */
export const LazyFlagImage = ({ iso2, alt, className }: LazyFlagImageProps) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const placeholderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = placeholderRef.current;
    if (!element) return;

    if (!('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        }
      },
      {
        rootMargin: '200px',
        threshold: 0,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  if (!shouldLoad) {
    return <div ref={placeholderRef} className={cn(styles.flagPlaceholder, className)} aria-label={alt || `${iso2} flag`} />;
  }

  return <FlagImage iso2={iso2} alt={alt} className={className} />;
};
